import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedDate } from 'react-intl';
import get from 'lodash/get';
import differenceInHours from 'date-fns/difference_in_hours';
import differenceInDays from 'date-fns/difference_in_days';
import differenceInMonths from 'date-fns/difference_in_months';

import ProductRequirements from 'Helpers/Product/ProductRequirements';

export default function ProductRelease({ product }) {
  const dates = ProductRequirements.ReleaseDates(product);
  const preorderDelivery = get(product, 'preorder_delivery', false);
  const earlyAccess = dates.earlyAccess;

  let showReleaseDateText = true;
  if (product.ztormId && product.download_date) showReleaseDateText = false;
  if (Date.now() < Date.parse(dates.playableFrom)) showReleaseDateText = false;

  let daysLeft = differenceInDays(new Date(dates.releaseDate), new Date());
  let monthsLeft = differenceInMonths(new Date(dates.releaseDate), new Date());

  if (earlyAccess && Date.now() < Date.parse(dates.playableFrom)) {
    daysLeft = differenceInDays(new Date(dates.playableFrom), new Date());
    monthsLeft = differenceInMonths(new Date(dates.playableFrom), new Date());
  }

  const earlyAccessDiff = differenceInHours(
    new Date(dates.releaseDate), new Date(dates.playableFrom),
  );

  if (!dates.prerelease) return false;

  if (earlyAccess && Date.now() >= Date.parse(dates.playableFrom)) {
    return (
      <div className="release-date-section">
        <span className="main-date">
          <FormattedMessage id="product-release.earlyaccess.available-now" defaultMessage="Available with early access now." />
        </span>&nbsp;
        <span className="playable-in-days">
          <FormattedMessage
            id="product-release.earlyaccess.released-on"
            defaultMessage="This {type} will be fully released on {date}."
            values={{
              type: product.display_type,
              date: <FormattedDate
                value={Date.parse(dates.releaseDate)}
                year="numeric"
                month="long"
                day="2-digit"
              />,
            }}
          />
        </span>
      </div>
    );
  }

  if (dates.prerelease && !product.no_release_date) {
    return (
      <div className="release-date-section">
        <span className="main-date">
          <FormattedMessage
            id="product-release.prerelease.custom"
            defaultMessage="{text}: {releaseDate}."
            values={{
              text: earlyAccess ?
                <FormattedMessage id="product-release.prerelease.text.early-access" defaultMessage="Available from" /> :
                <FormattedMessage id="product-release.prerelease.text" defaultMessage="Planned Release Date" />,
              releaseDate: dates.hideDate ?
                <FormattedDate
                  value={
                    earlyAccess ? Date.parse(dates.playableFrom) : Date.parse(dates.releaseDate)
                  }
                  year="numeric"
                /> :
                <FormattedDate
                  value={
                    earlyAccess ? Date.parse(dates.playableFrom) : Date.parse(dates.releaseDate)
                  }
                  year="numeric"
                  month="long"
                  day="2-digit"
                />,
            }}
          />&nbsp;
        </span>

        {monthsLeft < 2 && daysLeft >= 30 &&
          <span className="playable-in-days">
            <FormattedMessage
              id="product-release.prerelease.playable"
              defaultMessage="This {type} will be playable in approximately {daysLeft, plural, one {# day} other {# days}}{earlyaccess}"
              values={{
                daysLeft,
                type: product.display_type,
                earlyaccess: earlyAccess ?
                  <FormattedMessage
                    id="product-release.prerelease.playable.early-access"
                    defaultMessage=" with {earlyAccessDiff} Hours Early Access."
                    values={{ earlyAccessDiff }}
                  />
                  :
                  <span>.</span>,
              }}
            />&nbsp;
          </span>
        }
        {monthsLeft >= 2 &&
          <span className="playable-in-days">
            <FormattedMessage
              id="product-release.prerelease.playable-months"
              defaultMessage="This {type} will be playable in approximately {monthsLeft} months."
              values={{
                monthsLeft,
                type: product.display_type,
              }}
            />&nbsp;
          </span>
        }
        {showReleaseDateText &&
          <span className="product-delivered">
            {!preorderDelivery &&
              <FormattedMessage id="product-release.prerelease.delivered.on-purchase" defaultMessage="The product will be delivered to you on purchase." />
            }&nbsp;
            {preorderDelivery &&
              <FormattedMessage id="product-release.prerelease.delivered.on-release" defaultMessage="The product will be delivered on or before the release date." />
            }&nbsp;
            {product.slug.includes('monster-hunter-wilds') &&
              <FormattedMessage id="product-release.prerelease.delivered.preload" defaultMessage="Pre-load available now." />
            }
          </span>
        }
      </div>
    );
  }

  if (product.no_release_date && product.no_release_date_text) {
    return (
      <div className="release-date-section">
        <span className="main-date">
          <FormattedMessage
            id="product-release.prerelease"
            defaultMessage="Planned Release Date: {releaseDate}."
            values={{
              releaseDate: product.no_release_date_text,
            }}
          />
        </span>
      </div>
    );
  }

  return false;
}

ProductRelease.propTypes = {
  product: PropTypes.shape({}),
};

ProductRelease.defaultProps = {
  product: {},
};
